import {Village} from "../../shared/interfaces/village";
import {
  EngageMetricsTypes,
  EnhancedMetricsTypes,
  EventMetricsColumn,
  EventsMetricsRow
} from "./eventMetrics";
import {EventMetricsSummary} from "../../stores/domainStore/pages/models/eventMetricsSummary";
import {NoticeMetricsSummary} from "../../stores/domainStore/pages/models/noticeMetricsSummary";
import {NoticeMetricsRow, NoticeMetricsTypes} from "./noticeMetrics";
import {CalendarTypeOptions, IMetricsSummary} from "./metricTypes";

// a map of events, where the key is the village id
type GroupedData<TInput extends IMetricsSummary> = {
  [key: string]: TInput[];
};
// A lookup table for villages
type VillageMap = {
  [villageId: string]: Village;
};

/**
 * Summarize a list of events into computed metrics
 * @param events The events to summarize
 * @param selectedCalendarType The type of calendar selected
 * @param selectedVillageIds The list of selected villages
 * @param villages A full list of all villages
 */
export function summariseEventsIntoMetrics(
  events: EventMetricsSummary[],
  selectedCalendarType: CalendarTypeOptions,
  selectedVillageIds: string[],
  villages: Village[]
): EventsMetricsRow[] {
  if (events.length === 0) return [];

  const groupedEvents = groupByVillage(events, selectedVillageIds);
  const villageLookup = createVillageMap(villages);

  const metrics =
    selectedCalendarType === "Engage"
      ? EngageMetricsTypes
      : EnhancedMetricsTypes;

  // loop through each selected village, so that even villages with no events are included
  const dataRows = selectedVillageIds.map((v) => {
    const village = villageLookup[v];
    const events = groupedEvents[v] ?? [];
    return generateEventMetrics(events, metrics, village);
  });

  if (selectedVillageIds.length === 1) return dataRows;

  const totalsRow = generateEventMetrics(events, metrics, undefined, true);
  return [totalsRow, ...dataRows];
}

function generateEventMetrics(
  events: EventMetricsSummary[],
  metrics: EventMetricsColumn[],
  village?: Village,
  isTotalsRow: boolean = false
) {
  const computedMetrics = new EventsMetricsRow(village, isTotalsRow);

  // loop through each type of metric and calculate the value
  metrics.forEach((type) => {
    type.valueInit?.(events, computedMetrics);
  });

  return computedMetrics;
}

/**
 * Summarize a list of notices into computed metrics
 * @param notices The notices to summarize
 * @param selectedVillageIds The list of selected villages
 * @param villages A full list of all villages
 */
export function summariseNoticesIntoMetrics(
  notices: NoticeMetricsSummary[],
  selectedVillageIds: string[],
  villages: Village[]
): NoticeMetricsRow[] {
  if (notices.length === 0) return [];

  const groupedNotices = groupByVillage(notices, selectedVillageIds);
  const villageLookup = createVillageMap(villages);

  // loop through each selected village, so that even villages with no notices are included
  const dataRows = selectedVillageIds.map((v) => {
    const village = villageLookup[v];
    const notices = groupedNotices[v] ?? [];
    return generateNoticeMetrics(notices, village);
  });

  if (selectedVillageIds.length === 1) return dataRows;

  const totalsRow = generateNoticeMetrics(notices, undefined, true);
  return [totalsRow, ...dataRows];
}

function generateNoticeMetrics(
  notices: NoticeMetricsSummary[],
  village?: Village,
  isTotalsRow: boolean = false
) {
  const computedMetrics = new NoticeMetricsRow(village, isTotalsRow);

  // loop through each type of metric and calculate the value
  NoticeMetricsTypes.forEach((type) => {
    type.valueInit?.(notices, computedMetrics);
  });

  return computedMetrics;
}

function groupByVillage<TInput extends IMetricsSummary>(
  data: TInput[],
  selectedVillageIds: string[]
): GroupedData<TInput> {
  return data.reduce((acc: GroupedData<TInput>, record) => {
    // If an event has no villageIds, it applies to all villages
    const villageIds = record.villageIds ?? selectedVillageIds;
    villageIds.forEach((v) => {
      if (!acc[v]) {
        acc[v] = [];
      }
      acc[v].push(record);
    });

    return acc;
  }, {});
}

function createVillageMap(villages: Village[]): VillageMap {
  return villages.reduce((acc: VillageMap, village) => {
    acc[village.id] = village;
    return acc;
  }, {});
}
